import { Link, useParams } from 'react-router-dom';
import {
  useAssets,
  useChecklistTemplates,
  useDomain,
  useLogs,
  useProjects,
} from '@/lib/queries';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronRight } from 'lucide-react';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import LogList from '@/components/LogList';
import PhotoGrid from '@/components/PhotoGrid';

export default function DomainDetail() {
  const { slug } = useParams<{ slug: string }>();
  const q = useDomain(slug);
  const templates = useChecklistTemplates(slug);
  const logs = useLogs({ domain: slug, limit: 50 });
  const photos = useAssets({ domain: slug, limit: 60 });
  const allProjects = useProjects({});
  const queryClient = useQueryClient();

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const { domain, guides } = q.data;
  const tmpls = templates.data ?? [];

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['domain', slug] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-6">
        <header className="space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/domains" className="hover:text-foreground">
              Domains
            </Link>{' '}
            <span aria-hidden>·</span> {domain.slug}
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
            {domain.name}
          </h1>
          {domain.description ? (
            <p className="text-muted-foreground max-w-2xl">{domain.description}</p>
          ) : null}
        </header>

        <Tabs defaultValue="overview">
          {/* Tabs row scrolls horizontally on narrow screens; the
              first tab stays anchored visually. */}
          <TabsList className="overflow-x-auto -mx-1 px-1 max-w-full whitespace-nowrap">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="guides" data-testid="domain-tab-guides">
              Guides ({guides.length})
            </TabsTrigger>
            <TabsTrigger value="checklists" data-testid="domain-tab-checklists">
              Checklists ({tmpls.length})
            </TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <Card>
              <CardHeader className="space-y-1">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                  Identifier
                </p>
                <CardTitle className="font-display text-base">Slug</CardTitle>
                <CardDescription className="font-mono">{domain.slug}</CardDescription>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                Use the tabs above to navigate guides and — in later phases — checklists, logs, and
                photos for this domain.
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="guides">
            {guides.length === 0 ? (
              <p className="text-sm text-muted-foreground">No guides yet for this domain.</p>
            ) : (
              <ul
                className="divide-y divide-rule rounded-md border border-rule bg-card"
                data-testid="domain-guides-list"
              >
                {guides.map((g) => (
                  <li key={g.id}>
                    <Link
                      to={`/guides/${g.slug}`}
                      className="flex items-start justify-between gap-3 px-4 py-4 transition-colors hover:bg-accent/40"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {g.slug}
                        </p>
                        <p className="font-display text-base font-medium leading-tight">{g.title}</p>
                        {g.lastReviewed ? (
                          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                            Reviewed {g.lastReviewed}
                          </p>
                        ) : null}
                      </div>
                      <ChevronRight
                        className="h-4 w-4 text-muted-foreground shrink-0 mt-1"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="checklists">
            {tmpls.length === 0 ? (
              <p className="text-sm text-muted-foreground">No checklist templates for this domain.</p>
            ) : (
              <ul
                className="divide-y divide-rule rounded-md border border-rule bg-card"
                data-testid="domain-checklist-templates"
              >
                {tmpls.map((t) => (
                  <li key={t.id}>
                    <Link
                      to={`/checklists/templates/${t.slug}`}
                      className="flex items-start justify-between gap-3 px-4 py-4 hover:bg-accent/40 transition-colors"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                          {t.slug}
                        </p>
                        <p className="font-display text-base font-medium leading-tight">{t.title}</p>
                        {t.description ? (
                          <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>
                        ) : null}
                        <div className="flex flex-wrap gap-2 pt-1">
                          {t.season && t.season !== 'any' ? (
                            <Badge variant="secondary" className="font-mono text-[10px] tracking-wider">
                              {t.season}
                            </Badge>
                          ) : null}
                          {t.recurrenceRule ? (
                            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                              {t.recurrenceRule}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <ChevronRight
                        className="h-4 w-4 text-muted-foreground shrink-0 mt-1"
                        aria-hidden
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="logs">
            {logs.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <LogList
                logs={logs.data ?? []}
                emptyMessage="No logs in this domain yet."
              />
            )}
          </TabsContent>
          <TabsContent value="photos">
            {photos.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : (
              <PhotoGrid
                assets={photos.data ?? []}
                emptyMessage="No photos in this domain yet."
              />
            )}
          </TabsContent>
          <TabsContent value="projects">
            {(() => {
              const filtered = (allProjects.data ?? []).filter((p) => p.domainId === domain.id);
              if (filtered.length === 0) {
                return <p className="text-sm text-muted-foreground">No projects in this domain yet.</p>;
              }
              return (
                <ul
                  className="divide-y divide-rule rounded-md border border-rule bg-card"
                  data-testid="domain-projects"
                >
                  {filtered.map((p) => (
                    <li key={p.id}>
                      <Link
                        to={`/projects/${p.id}`}
                        className="flex items-start justify-between gap-3 px-4 py-4 hover:bg-accent/40 transition-colors"
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            {p.status} · {p.priority}
                          </p>
                          <p className="font-display text-base font-medium leading-tight">{p.title}</p>
                        </div>
                        <ChevronRight
                          className="h-4 w-4 text-muted-foreground shrink-0 mt-1"
                          aria-hidden
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
    </PullToRefresh>
  );
}
